import { useState } from 'react'
import { Link } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import useCustomerList from '../../utils/useCustomerList'
import { inspectQuery, rawQuery, chat } from '../../api'
import './QueryInspector.css'

export default function QueryInspector() {
  const [question, setQuestion] = useState('')
  const [customerId, setCustomerId] = useState('test')
  const { customers, error: customersError, reload: reloadCustomers } = useCustomerList(list => {
    if (list.length > 0) setCustomerId(list[0].customer_id)
  })
  const [result, setResult] = useState(null)
  const [rawSql, setRawSql] = useState('')
  const [rawResult, setRawResult] = useState(null)
  const [inspecting, setInspecting] = useState(false)
  const [runningRaw, setRunningRaw] = useState(false)
  const [activeTab, setActiveTab] = useState('inspect') // inspect | raw

  async function handleInspect(e) {
    e.preventDefault()
    if (!question.trim()) return
    setInspecting(true)
    setResult(null)
    try {
      // Try inspect endpoint first, fall back to chat
      let data
      try {
        data = await inspectQuery({ question, customer_id: customerId })
      } catch {
        data = await chat(question, customerId)
      }
      setResult(data)
    } catch (err) {
      setResult({ error: err.message })
    } finally {
      setInspecting(false)
    }
  }

  async function handleRawQuery(e) {
    e.preventDefault()
    if (!rawSql.trim()) return
    setRunningRaw(true)
    setRawResult(null)
    try {
      const data = await rawQuery(rawSql, customerId)
      setRawResult(data)
    } catch (err) {
      setRawResult({ error: err.message })
    } finally {
      setRunningRaw(false)
    }
  }

  const breadcrumbs = [
    { label: 'Admin', href: '/admin' },
    { label: 'Query Inspector' },
  ]
  const navActions = (
    <>
      <Link to="/admin" className="nav-btn">Dashboard</Link>
      
    </>
  )

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} actions={navActions} />

      <div className="qi-page">
        {/* Tab Switcher */}
        <div className="qi-tabs">
          <button className={`qi-tab ${activeTab === 'inspect' ? 'active' : ''}`} onClick={() => setActiveTab('inspect')}>
            🔍 Inspect Query
          </button>
          <button className={`qi-tab ${activeTab === 'raw' ? 'active' : ''}`} onClick={() => setActiveTab('raw')}>
            💻 Raw SQL
          </button>
        </div>

        {/* Customer Selector */}
        <div className="qi-customer-bar">
          <label className="qi-label">Customer:</label>
          <select className="qi-select" value={customerId} onChange={e => setCustomerId(e.target.value)}>
            {customers.map(c => (
              <option key={c.customer_id} value={c.customer_id}>{c.company_name} ({c.customer_id})</option>
            ))}
          </select>
          {customersError && <LoadError inline what="customers" error={customersError} onRetry={reloadCustomers} />}
        </div>

        {activeTab === 'inspect' && (
          <div className="qi-section">
            <form className="qi-input-row" onSubmit={handleInspect}>
              <input
                className="qi-input"
                type="text"
                placeholder="Enter a question… e.g. How many employees are in California?"
                value={question}
                onChange={e => setQuestion(e.target.value)}
                autoFocus
              />
              <button className="qi-run-btn" type="submit" disabled={inspecting}>
                {inspecting ? 'Inspecting…' : 'Inspect'}
              </button>
            </form>

            {result && (
              <div className="qi-result">
                {result.error ? (
                  <div className="qi-error">{result.error}</div>
                ) : (
                  <>
                    {/* Answer */}
                    <div className="qi-result-section">
                      <h4 className="qi-result-title">Answer</h4>
                      <div className="qi-answer">{result.answer || 'No answer returned'}</div>
                    </div>

                    {/* Timing */}
                    <div className="qi-result-row">
                      <span className="qi-result-label">Processing Time</span>
                      <span className="qi-result-value">{result.processing_time_ms?.toFixed(0) || '?'}ms</span>
                    </div>

                    {result.needs_clarification && (
                      <div className="qi-result-row">
                        <span className="qi-result-label">Needs Clarification</span>
                        <span className="qi-result-value" style={{ color: 'var(--amber)' }}>Yes</span>
                      </div>
                    )}

                    {/* SQL */}
                    {result.sql && (
                      <div className="qi-result-section">
                        <h4 className="qi-result-title">Generated SQL</h4>
                        <pre className="qi-code">{result.sql}</pre>
                      </div>
                    )}

                    {/* Data */}
                    {result.data && result.data.length > 0 && (
                      <div className="qi-result-section">
                        <h4 className="qi-result-title">Data ({result.row_count || result.data.length} rows)</h4>
                        <div className="qi-data-table-wrap">
                          <table className="qi-data-table">
                            <thead>
                              <tr>
                                {(result.columns || Object.keys(result.data[0])).map((col, i) => (
                                  <th key={i}>{typeof col === 'string' ? col : col.name || col}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {result.data.slice(0, 50).map((row, i) => (
                                <tr key={i}>
                                  {(result.columns || Object.keys(row)).map((col, j) => {
                                    const key = typeof col === 'string' ? col : col.name || col
                                    const val = row[key]
                                    return <td key={j}>{val !== null && val !== undefined ? String(val) : ''}</td>
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {result.data.length > 50 && (
                          <p className="qi-data-note">Showing 50 of {result.data.length} rows</p>
                        )}
                      </div>
                    )}

                    {/* Citations / Provenance */}
                    {result.citations && result.citations.length > 0 && (
                      <div className="qi-result-section">
                        <h4 className="qi-result-title">Provenance ({result.citations.length})</h4>
                        <div className="qi-citations">
                          {result.citations.map((cite, i) => (
                            <div key={i} className="qi-citation">{JSON.stringify(cite)}</div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Full Response JSON (collapsible) */}
                    <details className="qi-details">
                      <summary className="qi-details-summary">Full Response JSON</summary>
                      <pre className="qi-code qi-code-full">{JSON.stringify(result, null, 2)}</pre>
                    </details>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'raw' && (
          <div className="qi-section">
            <form className="qi-raw-form" onSubmit={handleRawQuery}>
              <textarea
                className="qi-raw-input"
                placeholder="SELECT COUNT(*) FROM cust_test_api_person_details WHERE addressState = 'CA'"
                value={rawSql}
                onChange={e => setRawSql(e.target.value)}
                rows={4}
              />
              <button className="qi-run-btn" type="submit" disabled={runningRaw}>
                {runningRaw ? 'Running…' : '▶ Execute'}
              </button>
            </form>

            {rawResult && (
              <div className="qi-result">
                {rawResult.error ? (
                  <div className="qi-error">{rawResult.error}</div>
                ) : (
                  <>
                    {rawResult.data && rawResult.data.length > 0 && (
                      <div className="qi-data-table-wrap">
                        <table className="qi-data-table">
                          <thead>
                            <tr>
                              {(rawResult.columns || Object.keys(rawResult.data[0])).map((col, i) => (
                                <th key={i}>{typeof col === 'string' ? col : col.name || col}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rawResult.data.slice(0, 100).map((row, i) => (
                              <tr key={i}>
                                {(rawResult.columns || Object.keys(row)).map((col, j) => {
                                  const key = typeof col === 'string' ? col : col.name || col
                                  return <td key={j}>{String(row[key] ?? '')}</td>
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div className="qi-result-row">
                      <span className="qi-result-label">Rows</span>
                      <span className="qi-result-value">{rawResult.row_count || rawResult.data?.length || 0}</span>
                    </div>
                    <details className="qi-details">
                      <summary className="qi-details-summary">Full Response JSON</summary>
                      <pre className="qi-code qi-code-full">{JSON.stringify(rawResult, null, 2)}</pre>
                    </details>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}
